// ── 토스트 알림 컴포넌트 ──────────────────────────────────────────────────────
// Context + Provider 패턴으로 전역 사용
// dark mode: success/error 색상 모두 다크 모드 대응
import React, { createContext, useContext, useMemo, useState } from 'react';

const ToastContext = createContext(null);

/**
 * 앱 루트에서 감싸는 Provider
 * showToast(message, type) — type: 'success' | 'error' | 'notice'(회색 안내 — 2026-10-24 상황판 요약 위젯, 그 밖의 값은 종전처럼 빨강)
 */
export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);

  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setItems((prev) => [...prev, { id, message, type }]);
    // 3초 후 자동 제거
    setTimeout(() => {
      setItems((prev) => prev.filter((x) => x.id !== id));
    }, 3000);
  };

  const value = useMemo(() => ({ showToast }), []);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* ── 토스트 컨테이너: 우하단 고정 ── */}
      <div className="fixed right-4 bottom-4 z-[9999] grid gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            className={[
              // 기본 레이아웃
              'flex items-center gap-2 px-3.5 py-2.5',
              'rounded-xl text-sm font-semibold text-white',
              'shadow-popup',
              // 애니메이션 (간단한 페이드인)
              'animate-[fadeInUp_0.2s_ease-out]',
              // type별 배경색
              t.type === 'success'
                ? 'bg-green-600 dark:bg-green-700'
                : t.type === 'notice'
                  ? 'bg-slate-700 dark:bg-slate-600'
                  : 'bg-red-600 dark:bg-red-700',
            ].join(' ')}
          >
            {/* 아이콘 */}
            {t.type === 'notice' ? (
              // 안내(i) 아이콘
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="flex-shrink-0">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                <circle cx="8" cy="4.9" r="0.9" fill="currentColor"/>
              </svg>
            ) : t.type === 'success' ? (
              // 체크 아이콘
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="flex-shrink-0">
                <path d="M3 8.5l3.5 3.5 6.5-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              // X 아이콘
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="flex-shrink-0">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** 토스트 훅 — ToastProvider 안에서만 사용 가능 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
